/**
 * TrustLevelSelector — Sprint 7 · Project Symphony
 *
 * Owner-facing trust configuration. Every option carries the exact
 * tagline the executive patch specifies. Changes are immediate and
 * reversible.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Circle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Card } from "@/shared/ui/card";
import { setTrustLevel, type AutopilotDashboard, type TrustLevel } from "../api";

const LEVELS: { key: TrustLevel; label: string; tagline: string }[] = [
  {
    key: "advisor",
    label: "Advisor",
    tagline:
      "Orkexra prepares everything. You approve strategy, assets, schedule, and publishing.",
  },
  {
    key: "assistant",
    label: "Assistant",
    tagline:
      "Orkexra approves strategy, assets, and the schedule. You approve publishing.",
  },
  {
    key: "operator",
    label: "Operator",
    tagline:
      "Orkexra operates the campaign. You approve premium-cost decisions only.",
  },
  {
    key: "executive",
    label: "Executive",
    tagline:
      "Orkexra operates continuously. You receive daily and weekly briefs.",
  },
];

const LEVEL_RANK: Record<TrustLevel, number> = {
  advisor: 1,
  assistant: 2,
  operator: 3,
  executive: 4,
};

export function TrustLevelSelector({
  dashboard,
}: {
  dashboard: AutopilotDashboard;
}): JSX.Element {
  const qc = useQueryClient();
  const change = useMutation({
    mutationFn: (level: TrustLevel) => setTrustLevel(level),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["autopilot-dashboard"] }),
    onError: (error: Error) => toast.error(
      error.message || "We couldn't change the Autopilot trust level.",
    ),
  });

  return (
    <Card className="p-5" data-testid="trust-level-selector">
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
        Trust Level
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {LEVELS.map((level) => {
          const selected = dashboard.trust_level === level.key;
          const overCeiling =
            LEVEL_RANK[level.key] > LEVEL_RANK[dashboard.autopilot_max_level];
          const overReadiness =
            LEVEL_RANK[level.key] > LEVEL_RANK[dashboard.readiness_max_level];
          return (
            <button
              key={level.key}
              type="button"
              onClick={() => change.mutate(level.key)}
              disabled={change.isPending || selected || overCeiling || overReadiness}
              title={overCeiling ? "Available on any paid plan" : overReadiness ? "Complete readiness requirements" : undefined}
              aria-describedby={overCeiling || overReadiness ? `trust-level-${level.key}-ceiling` : undefined}
              data-testid={`trust-level-${level.key}`}
              className={`
                text-left rounded-md border p-3 transition-colors
                ${selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:border-primary/40 hover:bg-muted/40"}
                disabled:opacity-70
              `}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold">{level.label}</span>
                {selected && (
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                {level.tagline}
              </p>
              {overCeiling && (
                <p
                  id={`trust-level-${level.key}-ceiling`}
                  className="mt-2 text-xs font-medium text-amber-600"
                >
                  Available on any paid plan
                </p>
              )}
              {!overCeiling && overReadiness && (
                <p
                  id={`trust-level-${level.key}-ceiling`}
                  className="mt-2 text-xs font-medium text-amber-600"
                >
                  Complete readiness requirements
                </p>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-5 border-t border-border pt-4" data-testid="autopilot-readiness">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Readiness</p>
          <p className="text-xs text-muted-foreground">
            Ready through {LEVELS.find((level) => level.key === dashboard.readiness_max_level)?.label}
          </p>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dashboard.readiness_checks.map((check) => (
            <Link
              key={check.key}
              to={check.action_path}
              className="flex items-start gap-2 rounded-md border border-border/70 p-2 text-xs hover:border-primary/40"
            >
              {check.passed ? (
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden />
              ) : (
                <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
              )}
              <span>
                <span className="block font-medium">{check.label}</span>
                <span className="text-muted-foreground">Required for {check.required_for}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </Card>
  );
}
